import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalCloseButton,
  Spinner,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import {
  FiArrowLeft,
  FiArrowRight,
  FiCheckCircle,
  FiCreditCard,
} from "react-icons/fi";

import { axiosClient } from "../client/http-client";
import { getAuthToken } from "../client/token-utils";
import { useMutation } from "@tanstack/react-query";

import { MotionReveal, WizardProgress, WizardTitle, toWizardSteps } from "./ui";
import ApplicationStartError from "./applicationStartError";

import GeneralStep from "./RequestCredit/ApplicationSteps/General/generalStep";
import LicenseStep from "./RequestCredit/ApplicationSteps/License/licenseStep";
import ContactsStep from "./RequestCredit/ApplicationSteps/Contacts/contactsStep";
import UploadsStep from "./RequestCredit/ApplicationSteps/Uploads/uploadsStep";
import SignatureStep from "./RequestCredit/ApplicationSteps/Signature/signatureStep";
import RequestsStep from "./RequestCredit/ApplicationSteps/Requests/requestsStep";

// types for the application in  request credit
import BankStep from "./RequestCredit/ApplicationSteps/Bank/bankStep";
import SuppliersStep from "./RequestCredit/ApplicationSteps/Suppliers/suppliersStep";

import {
  GeneralInfo,
  ContactsInfo,
  LicenseInfo,
  Upload,
  Requests,
  Signatures,
} from "../types/requestCreditDataTypes";

import { BankInfo, Suppliers } from "../types/requestCreditDataTypes";

import { useStepTimer } from "../utils/timer";

import {
  validateGeneralStep,
  validateContactsStep,
  validateReferencesStep,
  validateUploadsStep,
} from "./RequestCredit/stepsValidators/validators";

interface requestCreditApplicationModalProps {
  isOpen: boolean;
  onClose: () => void;
  OnOpen: () => void;
  applicationData: any;
  refetchApplication: () => void;
  lookingForApplications: boolean;
  isApplicationActive: boolean;
  setIsApplicationActive: (data: boolean) => void;
  refetchApplications: () => void;
}

// modal steps, it consists of 3 steps each step contains multi steps
const modalSteps: { [key: string]: string[] } = {
  general: ["general", "license"],
  contacts: ["contacts"],
  references: ["bank", "suppliers"],
  uploads: ["uploads", "signatures", "requests"],
};

// get the main 4 steps names to use in the stepper
const mainSteps = Object.keys(modalSteps);

/** Sub-group heading inside a wizard step: h4 + optional lede + hairline (§7.4). */
const StepGroup: React.FC<{
  title: string;
  lede?: string;
  children: React.ReactNode;
}> = ({ title, lede, children }) => (
  <Box as="section">
    <Flex align="baseline" gap={3} wrap="wrap">
      <Heading as="h3" size="xs">
        {title}
      </Heading>
      {lede && (
        <Text textStyle="body.sm" color="text.muted">
          {lede}
        </Text>
      )}
    </Flex>
    <Divider mt={3} mb={5} />
    {children}
  </Box>
);

const RequestCreditApplicationModal: React.FC<
  requestCreditApplicationModalProps
> = ({
  OnOpen,
  isOpen,
  onClose,
  applicationData,
  refetchApplication,
  lookingForApplications,
  isApplicationActive,
  setIsApplicationActive,
  refetchApplications,
}) => {
  // get user token from cookies
  const token = getAuthToken();

  // current active step
  const [activeStep, setActiveStep] = useState(0);

  // the steps if the active step
  const currentSteps = modalSteps[mainSteps[activeStep]];

  // toast to view notifications
  const toast = useToast();

  // timer hook to calculate time spent on each step
  const { stopTimer, startTimer } = useStepTimer(isOpen, activeStep);

  // first field of the first step — receives focus when the modal opens
  const firstFieldRef = useRef<HTMLInputElement>(null);

  // data of the active applications
  const [stepsData, setStepsData] = useState<any>({});

  // state to handle the uploaded files
  const [documents_info, setDocumentsInfo] = useState<Upload>({
    license: undefined,
    owner_pp: undefined,
    owner_eid: undefined,
    hasVisa: "no",
    owner_visa: undefined,
    hasPowerOfAttorney: "no",
    power_of_attorney: undefined,
    hasVatCert: "no",
    hasBankCert: "no",
    bank_certificate: undefined,
  });

  const [isStepSaved, setIsStepSaved] = useState<boolean>(false);

  // Effect to set the steps data when data is available
  useEffect(() => {
    if (applicationData) {
      setStepsData({
        general: applicationData.general_info,
        contacts: applicationData.contacts_info,
        license: applicationData.license_info,
        uploads: applicationData.documents_info,
        bank: applicationData.bank_info,
        suppliers: applicationData.suppliers_info,
        signatures: applicationData.signatures_info,
        requests: applicationData.requests_info,
      });
    }
  }, [applicationData]);

  useEffect(() => {
    setIsStepSaved(false);
    if (mainSteps[activeStep] === "general") {
      if (
        validateGeneralStep({
          general: stepsData["general"],
          license: stepsData["license"],
        })
      ) {
        setIsStepSaved(true);
      }
    } else if (mainSteps[activeStep] === "contacts") {
      if (
        validateContactsStep({
          contacts: stepsData["contacts"],
        })
      ) {
        setIsStepSaved(true);
      }
    } else if (mainSteps[activeStep] === "references") {
      if (
        validateReferencesStep({
          bank: stepsData["bank"],
          suppliers: stepsData["suppliers"],
        })
      ) {
        setIsStepSaved(true);
      }
    } else {
      if (
        validateUploadsStep({
          signatures: stepsData["signatures"],
          requests: stepsData["requests"],
        })
      ) {
        setIsStepSaved(true);
      }
    }
  }, [activeStep, stepsData]);

  //1- function create a request credit application
  const createRequestCreditApplication = async () => {
    return await axiosClient.post(
      "/applications/request-credit/create",
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  };

  // 1-- use mutation for request credit application creation
  const {
    mutate: mutateRequestCreditApplication,
    isPending: isCreatingApplication,
    error: createApplicationError,
  } = useMutation({
    mutationKey: ["requestCreditApplication"],
    mutationFn: createRequestCreditApplication,
    onSuccess: (data) => {
      setIsApplicationActive(true);
      OnOpen();
      toast({
        title: data.data.message,
        status: data.status === 200 ? "warning" : "success",
        duration: 3000,
        isClosable: true,
      });
    },
  });

  // time spent updating
  // 2- function to update time spent on each step
  const updateSpentTime = async (time: number) => {
    return await axiosClient.post(
      `/applications/request-credit/update-time/${mainSteps[activeStep]}`,
      { time_spent: time },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  };

  // 2--use mutation for updating time spent
  const { mutate: updateTimeSpent, isPending: isUpdatingTime } = useMutation({
    mutationKey: ["requestCreditApplication-Time" + mainSteps[activeStep]],
    mutationFn: updateSpentTime,
    onError: (error: { response: { data: { message: [string] } } }) => {
      if (typeof error.response.data.message === "string") {
        return toast({
          title: error.response.data.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } else {
        error.response.data.message.forEach((message: string) => {
          toast({
            title: message,
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        });
      }
    },
  });

  // data updating
  // 1- function submit data for the application step
  const submitData = async () => {
    const reqResponse = await Promise.all(
      currentSteps.map(async (step) => {
        if (stepsData[step]) {
          const response = await axiosClient.post(
            `/applications/request-credit/save-progress/${step}`,
            step === "uploads" ? documents_info : stepsData[step],
            {
              headers: {
                // handle file uploads in uploads step only
                "Content-Type":
                  step === "uploads"
                    ? "multipart/form-data"
                    : "application/json",
                Authorization: `Bearer ${token}`,
              },
            }
          );
          return response.data;
        }
        return null;
      })
    );
    return reqResponse.filter((res) => res !== null);
  };

  // 2-- use mutation for updating steps data
  const { mutate, isPending: isMutating } = useMutation({
    mutationKey: ["requestCreditApplication" + mainSteps[activeStep]],
    mutationFn: submitData,
    onSuccess: (data) => {
      const timeSpent = stopTimer(); // Call stopTimer and get the time spent on the current step
      updateTimeSpent(timeSpent);
      // if the data.message on each response in the array were the same return only one
      if (data.every((response: any) => response.message === data[0].message)) {
        toast({
          title: data[0].message,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } else {
        data.forEach((response: any) => {
          toast({
            title: response.data.message,
            status: "success",
            duration: 5000,
            isClosable: true,
          });
        });
      }
    },
    onError: (error: { response: { data: { message: [string] } } }) => {
      if (typeof error.response.data.message === "string") {
        return toast({
          title: error.response.data.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } else {
        error.response.data.message.forEach((message: string) => {
          toast({
            title: message,
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        });
      }
    },
  });

  // finishing application
  // 4- function that handles the application finishing
  const FinishApplication = async () => {
    return await axiosClient.post(
      `/applications/request-credit/finish`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  };

  // 4-- use mutation for application finishing
  const { mutate: finishApplication, isPending: isFinishing } = useMutation({
    mutationKey: ["requestCredifApplication-Finish"],
    mutationFn: FinishApplication,
    onSuccess: (data) => {
      setIsApplicationActive(false);

      // empty the stepsData
      setStepsData({});

      // remove uploaded files
      setDocumentsInfo({
        license: undefined,
        owner_pp: undefined,
        owner_eid: undefined,
        hasVisa: "no",
        owner_visa: undefined,
        hasPowerOfAttorney: "no",
        power_of_attorney: undefined,
        hasVatCert: "no",
        hasBankCert: "no",
        bank_certificate: undefined,
      });

      // reset to the first step
      setActiveStep(0);

      // send notification
      toast({
        title: data.data.message,
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      refetchApplication();
      refetchApplications();
      onClose();
    },
    onError: (error: { response: { data: { message: [string] } } }) => {
      if (typeof error.response.data.message === "string") {
        return toast({
          title: error.response.data.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } else {
        error.response.data.message.forEach((message: string) => {
          toast({
            title: message,
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        });
      }
    },
  });

  // movement between steps
  // Function to handle moving to the next step
  const nextStep = () => {
    if (activeStep < mainSteps.length - 1) {
      setActiveStep(activeStep + 1); // Move to the next step
      startTimer(); // Start timing the new step
    }
  };

  // Function to handle moving to the previous step
  const prevStep = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1); // Move to the previous step
      startTimer(); // Restart the timer for the previous step
    }
  };

  const handleSubmitData = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    mutate();
  };

  const isCurrentStepValid = () => {
    switch (activeStep) {
      case 0:
        return validateGeneralStep({
          general: stepsData["general"],
          license: stepsData["license"],
        });
      case 1:
        return validateContactsStep({ contacts: stepsData["contacts"] });

      case 2:
        return validateReferencesStep({
          bank: stepsData["bank"],
          suppliers: stepsData["suppliers"],
        });
      case 3:
        return validateUploadsStep({
          signatures: stepsData["signatures"],
          requests: stepsData["requests"],
        });
      default:
        return false;
    }
  };

  return (
    <>
      {/* Choice card trigger (§7.4) — rendered by createApplicationModal inside its Stack */}
      <Button
        variant="secondary"
        h="auto"
        w="full"
        p={5}
        textAlign="left"
        justifyContent="flex-start"
        whiteSpace="normal"
        borderRadius="lg"
        data-group
        _hover={{
          boxShadow: "0 0 0 1.5px var(--chakra-colors-leaf-500)",
          bg: "leaf.50",
          _focusVisible: { boxShadow: "outline" },
          _disabled: {
            boxShadow: "0 0 0 1px var(--chakra-colors-border-strong)",
            bg: "surface.card",
          },
        }}
        // Busy state keeps the card content mounted (no isLoading swap) so the card never collapses to a pill.
        isDisabled={isCreatingApplication || lookingForApplications || isApplicationActive}
        aria-busy={isCreatingApplication || lookingForApplications}
        onClick={() => {
          mutateRequestCreditApplication();
        }}
      >
        {/* Spans only inside the <button> (phrasing content); display is set explicitly. */}
        <HStack as="span" display="flex" spacing={4} w="full" align="center">
          <Flex
            as="span"
            display="flex"
            w="44px"
            h="44px"
            flexShrink={0}
            borderRadius="md"
            bg="navy.50"
            color="navy.700"
            align="center"
            justify="center"
            aria-hidden
          >
            <Icon as={FiCreditCard} boxSize="20px" />
          </Flex>
          <Box as="span" display="block" flex={1} minW={0}>
            <Text as="span" display="block" textStyle="h3" color="text.primary">
              Request a credit account
            </Text>
            <Text as="span" display="block" textStyle="body.sm" color="text.muted" fontWeight={500} mt={0.5}>
              {isCreatingApplication
                ? "Creating application…"
                : lookingForApplications
                  ? "Checking for an active application…"
                  : "Open a credit account for your outlet — licence, bank and supplier references, then sign."}
            </Text>
          </Box>
          {isCreatingApplication || lookingForApplications ? (
            <Spinner as="span" size="sm" color="text.muted" flexShrink={0} aria-hidden />
          ) : (
            <Icon
              as={FiArrowRight}
              boxSize="18px"
              color="text.muted"
              flexShrink={0}
              aria-hidden
              transition="transform 160ms cubic-bezier(0.2, 0.8, 0.2, 1)"
              _groupHover={{ transform: "translateX(2px)", color: "navy.900" }}
            />
          )}
        </HStack>
      </Button>
      {createApplicationError && <ApplicationStartError error={createApplicationError} />}

      <Modal
        scrollBehavior="inside"
        closeOnOverlayClick={false}
        size={"4xl"}
        isCentered
        onClose={onClose}
        isOpen={isOpen}
        motionPreset="slideInBottom"
        // The first-field ref only exists on step 0; on later steps focus goes to the first field of the
        // step body (data-autofocus-inside on ModalBody) instead of the close button.
        initialFocusRef={activeStep === 0 ? firstFieldRef : undefined}
      >
        <ModalOverlay />
        <ModalContent as={"form"} onSubmit={handleSubmitData}>
          <ModalHeader p={0} border="none">
            <WizardTitle eyebrow="NEW APPLICATION" title="Request a credit account" />
          </ModalHeader>
          <ModalCloseButton aria-label="Close" />
          <WizardProgress steps={toWizardSteps(mainSteps)} activeStep={activeStep} isStepSaved={isStepSaved} />
          <ModalBody px={{ base: 5, md: 8 }} py={6} data-autofocus-inside>
            {/* render the components of the current step — remount on step change for the fade-up */}
            <MotionReveal key={activeStep}>
              {activeStep === 0 && (
                <Stack spacing={10}>
                  <StepGroup
                    title="General information"
                    lede="Outlet identity, billing and delivery addresses"
                  >
                    <GeneralStep
                      savedData={stepsData.general ?? ({} as GeneralInfo)}
                      updateData={(data: GeneralInfo) => {
                        setStepsData({
                          ...stepsData,
                          general: data,
                        });
                      }}
                      initialFocusRef={firstFieldRef}
                    />
                  </StepGroup>

                  <StepGroup
                    title="Trade licence & VAT"
                    lede="As printed on your trade licence"
                  >
                    <LicenseStep
                      savedData={stepsData.license ?? ({} as LicenseInfo)}
                      updateData={(data: LicenseInfo) => {
                        setStepsData({
                          ...stepsData,
                          license: data,
                        });
                      }}
                    />
                  </StepGroup>
                </Stack>
              )}

              {activeStep === 1 && (
                <StepGroup
                  title="Contacts"
                  lede="Trade licence members and who we should reach"
                >
                  <ContactsStep
                    savedData={stepsData.contacts ?? ([] as ContactsInfo)}
                    updateData={(data: ContactsInfo) => {
                      setStepsData({
                        ...stepsData,
                        contacts: data,
                      });
                    }}
                  />
                </StepGroup>
              )}

              {activeStep === 2 && (
                <Stack spacing={10}>
                  <StepGroup
                    title="Bank details"
                    lede="The account Bidfood should hold on file"
                  >
                    <BankStep
                      savedData={stepsData.bank ?? ({} as BankInfo)}
                      updateData={(data: BankInfo) => {
                        setStepsData({
                          ...stepsData,
                          bank: data,
                        });
                      }}
                    />
                  </StepGroup>

                  <StepGroup
                    title="Supplier references"
                    lede="Suppliers who can vouch for your trading history"
                  >
                    <SuppliersStep
                      savedData={stepsData.suppliers ?? ({} as Suppliers)}
                      updateData={(data: Suppliers) => {
                        setStepsData({
                          ...stepsData,
                          suppliers: data,
                        });
                      }}
                    />
                  </StepGroup>
                </Stack>
              )}

              {activeStep === 3 && (
                <Stack spacing={10}>
                  <StepGroup
                    title="Documents"
                    lede="Trade licence, identity and supporting papers"
                  >
                    <UploadsStep
                      savedData={
                        stepsData.uploads ??
                        ([] as {
                          fieldname: string;
                          originalname: string;
                        }[])
                      }
                      documents_info={documents_info ?? ({} as Upload)}
                      setDocumentsInfo={setDocumentsInfo}
                    />
                  </StepGroup>

                  <StepGroup
                    title="Authorised signatures"
                    lede="People who may sign on behalf of the outlet"
                  >
                    <SignatureStep
                      savedData={stepsData.signatures ?? ([] as Signatures)}
                      updateData={(data: Signatures) => {
                        setStepsData({
                          ...stepsData,
                          signatures: data,
                        });
                      }}
                    />
                  </StepGroup>

                  <StepGroup
                    title="Credit request"
                    lede="Limit, period and how you will pay"
                  >
                    <RequestsStep
                      savedData={stepsData.requests ?? ({} as Requests)}
                      updateData={(data: Requests) => {
                        setStepsData({
                          ...stepsData,
                          requests: data,
                        });
                      }}
                    />
                  </StepGroup>
                </Stack>
              )}
            </MotionReveal>
          </ModalBody>
          <ModalFooter flexWrap="wrap">
            <Button
              variant="ghost"
              leftIcon={<FiArrowLeft />}
              onClick={prevStep}
              isDisabled={activeStep === 0}
            >
              Back
            </Button>

            <HStack spacing={3} flexWrap="wrap" justify="flex-end" ml="auto">
              <Button
                variant="secondary"
                type="submit"
                isDisabled={activeStep >= mainSteps.length}
                isLoading={isMutating || isUpdatingTime}
              >
                Save progress
              </Button>

              {activeStep === mainSteps.length - 1 ? (
                <Button
                  variant="primary"
                  leftIcon={<FiCheckCircle />}
                  isLoading={isFinishing}
                  onClick={() => {
                    finishApplication();
                  }}
                  isDisabled={!(isCurrentStepValid() && isStepSaved)}
                >
                  Submit application
                </Button>
              ) : (
                <Button
                  variant="primary"
                  rightIcon={<FiArrowRight />}
                  onClick={nextStep}
                  isDisabled={!(isCurrentStepValid() && isStepSaved)}
                >
                  Next
                </Button>
              )}
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default RequestCreditApplicationModal;
